import { useState, useEffect } from 'react'
import { useParams, Link } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'
import { useQuery } from '@tanstack/react-query'
import { motion, AnimatePresence } from 'framer-motion'
import {
    AcademicCapIcon,
    CalendarIcon,
    CurrencyRupeeIcon,
    BookOpenIcon,
    TrophyIcon,
    StarIcon,
    CheckCircleIcon,
    ClockIcon,
    UsersIcon,
    BuildingOfficeIcon,
    MapPinIcon,
    PhoneIcon,
    EnvelopeIcon,
    GlobeAltIcon,
    DocumentTextIcon,
    PlayIcon,
    ArrowDownTrayIcon
} from '@heroicons/react/24/outline'
import { StarIcon as StarIconSolid } from '@heroicons/react/24/solid'
import { courseAPI } from '../../services/api'
import LoadingSpinner from '../../components/Common/LoadingSpinner'
import ErrorMessage from '../../components/Common/ErrorMessage'
import EnquiryModal from '../../components/Common/EnquiryModal'

const CourseDetail = () => {
    const { slug } = useParams()
    const [activeTab, setActiveTab] = useState('overview')
    const [enquiryModal, setEnquiryModal] = useState({
        isOpen: false,
        course: null,
        type: ''
    })

    // Fetch course data
    const { data: courseData, isLoading, error } = useQuery({
        queryKey: ['course', slug],
        queryFn: () => courseAPI.getCourse(slug),
        enabled: !!slug
    })

    const course = courseData?.data?.course

    // Fetch comprehensive data for each tab
    const { data: overviewData } = useQuery({
        queryKey: ['course-overview', course?._id],
        queryFn: () => courseAPI.getCourseOverview(course?._id),
        enabled: !!course?._id && activeTab === 'overview'
    })

    const { data: curriculumData } = useQuery({
        queryKey: ['course-curriculum', course?._id],
        queryFn: () => courseAPI.getCourseCurriculum(course?._id),
        enabled: !!course?._id && activeTab === 'curriculum'
    })

    const { data: eligibilityData } = useQuery({
        queryKey: ['course-eligibility', course?._id],
        queryFn: () => courseAPI.getCourseEligibility(course?._id),
        enabled: !!course?._id && activeTab === 'eligibility'
    })

    const { data: feeStructureData } = useQuery({
        queryKey: ['course-fee-structure', course?._id],
        queryFn: () => courseAPI.getCourseFeeStructure(course?._id),
        enabled: !!course?._id && activeTab === 'fee-structure'
    })


    const { data: scholarshipsData } = useQuery({
        queryKey: ['course-scholarships', course?._id],
        queryFn: () => courseAPI.getCourseScholarships(course?._id),
        enabled: !!course?._id && activeTab === 'scholarships'
    })

    const handleEnquiryClick = (type = 'enquiry') => {
        setEnquiryModal({
            isOpen: true,
            course: course,
            type: type
        })
    }

    const handleCloseEnquiryModal = () => {
        setEnquiryModal({
            isOpen: false,
            course: null,
            type: ''
        })
    }

    const renderOverviewTab = () => {
        const overview = overviewData?.data?.course
        const hasDescription = overview?.description || course.description
        const hasHighlights = overview?.highlights && overview.highlights.length > 0
        const hasLearningOutcomes = overview?.learning_outcomes && overview.learning_outcomes.length > 0
        const hasCourseInfo = course.duration || course.level || course.specialization
        const hasUniversity = course.universityId

        if (!hasDescription && !hasHighlights && !hasLearningOutcomes && !hasCourseInfo && !hasUniversity) {
            return (
                <div className="bg-white rounded-xl shadow-soft p-8 text-center">
                    <BookOpenIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">Overview Not Available</h3>
                    <p className="text-gray-600">Overview details are not available for this course.</p>
                </div>
            )
        }

        return (
            <div className="space-y-8">
                {/* Course Description */}
                {hasDescription && (
                    <div className="bg-white rounded-xl shadow-soft p-8">
                        <h3 className="text-2xl font-bold text-gray-900 mb-6">Course Overview</h3>
                        <div className="prose max-w-none">
                            <div
                                className="text-gray-700 leading-relaxed"
                                dangerouslySetInnerHTML={{ __html: overview?.description || course.description }}
                            />
                        </div>
                    </div>
                )}

                {/* Course Information */}
                {hasCourseInfo && (
                    <div className="bg-white rounded-xl shadow-soft p-4 sm:p-8 mb-6 sm:mb-8">
                        <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4 sm:mb-6">Course Information</h3>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
                            {course.duration && (
                                <div className="bg-gradient-to-br from-primary-50 to-primary-100 rounded-xl shadow-soft p-4 sm:p-6 text-center border border-primary-200">
                                    <ClockIcon className="w-8 h-8 sm:w-12 sm:h-12 text-primary-600 mx-auto mb-3 sm:mb-4" />
                                    <h4 className="text-base sm:text-lg font-semibold text-gray-900">Duration</h4>
                                    <p className="text-lg sm:text-2xl font-bold text-primary-600">{course.duration}</p>
                                </div>
                            )}
                            {course.level && (
                                <div className="bg-gradient-to-br from-secondary-50 to-secondary-100 rounded-xl shadow-soft p-4 sm:p-6 text-center border border-secondary-200">
                                    <AcademicCapIcon className="w-8 h-8 sm:w-12 sm:h-12 text-secondary-600 mx-auto mb-3 sm:mb-4" />
                                    <h4 className="text-base sm:text-lg font-semibold text-gray-900">Level</h4>
                                    <p className="text-lg sm:text-2xl font-bold text-secondary-600">{course.level}</p>
                                </div>
                            )}
                            {course.specialization && course.specialization.length > 0 && (
                                <div className="bg-gradient-to-br from-accent-50 to-accent-100 rounded-xl shadow-soft p-4 sm:p-6 text-center border border-accent-200 sm:col-span-2 lg:col-span-1">
                                    <BookOpenIcon className="w-8 h-8 sm:w-12 sm:h-12 text-accent-600 mx-auto mb-3 sm:mb-4" />
                                    <h4 className="text-base sm:text-lg font-semibold text-gray-900">Specializations</h4>
                                    <p className="text-lg sm:text-2xl font-bold text-accent-600">
                                        {course.specialization.length} {course.specialization.length === 1 ? 'Specialization' : 'Specializations'}
                                    </p>
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* Degree Information */}
                {course.degreeId && (
                    <div className="bg-white rounded-xl shadow-soft p-4 sm:p-8 mb-6 sm:mb-8">
                        <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4 sm:mb-6">Degree Information</h3>
                        <div className="flex flex-col sm:flex-row items-start sm:items-center space-y-4 sm:space-y-0 sm:space-x-4">
                            <div className="bg-gradient-to-br from-primary-50 to-primary-100 rounded-lg p-3 sm:p-4 flex-shrink-0">
                                <AcademicCapIcon className="w-8 h-8 sm:w-12 sm:h-12 text-primary-600" />
                            </div>
                            <div className="flex-1 min-w-0">
                                <h4 className="text-lg sm:text-xl font-semibold text-gray-900">{course.degreeId.name}</h4>
                                {course.degreeId.shortName && (
                                    <p className="text-gray-600 mb-2 text-sm sm:text-base">({course.degreeId.shortName})</p>
                                )}
                                {course.degreeId.description && (
                                    <p className="text-gray-700 mb-2 text-sm sm:text-base">{course.degreeId.description}</p>
                                )}
                                <div className="flex flex-wrap gap-2">
                                    {course.degreeId.level && (
                                        <span className="inline-flex items-center px-2 sm:px-3 py-1 rounded-full text-xs sm:text-sm font-medium bg-blue-100 text-blue-800">
                                            {course.degreeId.level}
                                        </span>
                                    )}
                                    {course.degreeId.duration && (
                                        <span className="inline-flex items-center px-2 sm:px-3 py-1 rounded-full text-xs sm:text-sm font-medium bg-green-100 text-green-800">
                                            {course.degreeId.duration}
                                        </span>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* University Information */}
                {course.universityId && (
                    <div className="bg-white rounded-xl shadow-soft p-4 sm:p-8 mb-6 sm:mb-8">
                        <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4 sm:mb-6">University Information</h3>
                        <div className="flex flex-col sm:flex-row items-start sm:items-center space-y-4 sm:space-y-0 sm:space-x-4">
                            {course.universityId.logo?.url && (
                                <img
                                    src={course.universityId.logo.url}
                                    alt={course.universityId.name}
                                    className="w-12 h-12 sm:w-16 sm:h-16 object-contain flex-shrink-0"
                                />
                            )}
                            <div className="flex-1 min-w-0">
                                <h4 className="text-lg sm:text-xl font-semibold text-gray-900">{course.universityId.name}</h4>
                                <Link
                                    to={`/universities/${course.universityId.slug}`}
                                    className="text-primary-600 hover:text-primary-700 font-medium text-sm sm:text-base"
                                >
                                    View University Details →
                                </Link>
                            </div>
                        </div>
                    </div>
                )}

                {/* Specializations */}
                {course.specialization && course.specialization.length > 0 && (
                    <div className="bg-white rounded-xl shadow-soft p-4 sm:p-8 mb-6 sm:mb-8">
                        <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4 sm:mb-6">Specializations</h3>
                        <div className="flex flex-wrap gap-2">
                            {course.specialization.map((spec, index) => {
                                const colors = [
                                    'bg-blue-100 text-blue-800',
                                    'bg-green-100 text-green-800',
                                    'bg-purple-100 text-purple-800',
                                    'bg-pink-100 text-pink-800',
                                    'bg-indigo-100 text-indigo-800',
                                    'bg-yellow-100 text-yellow-800'
                                ]
                                const colorClass = colors[index % colors.length]
                                return (
                                    <span
                                        key={index}
                                        className={`inline-flex items-center px-2 sm:px-3 py-1 rounded-full text-xs sm:text-sm font-medium ${colorClass}`}
                                    >
                                        {spec}
                                    </span>
                                )
                            })}
                        </div>
                    </div>
                )}
            </div>
        )
    }

    const renderCurriculumTab = () => {
        const curriculum = curriculumData?.data?.curriculum

        if (!curriculum && !course.curriculum) {
            return (
                <div className="bg-white rounded-xl shadow-soft p-4 sm:p-8 text-center">
                    <BookOpenIcon className="w-12 h-12 sm:w-16 sm:h-16 text-gray-400 mx-auto mb-4" />
                    <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-2">Curriculum Not Available</h3>
                    <p className="text-gray-600 text-sm sm:text-base">Curriculum details are not available for this course.</p>
                </div>
            )
        }

        return (
            <div className="space-y-6 sm:space-y-8">
                {/* Curriculum Content */}
                <div className="bg-white rounded-xl shadow-soft p-4 sm:p-8">
                    <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4 sm:mb-6">Course Curriculum</h3>
                    <div className="prose max-w-none prose-sm sm:prose-base">
                        <div dangerouslySetInnerHTML={{ __html: curriculum || course.curriculum }} />
                    </div>
                </div>
            </div>
        )
    }

    const renderEligibilityTab = () => {
        const eligibility = eligibilityData?.data?.eligibility_details

        if (!eligibility && !course.eligibility) {
            return (
                <div className="bg-white rounded-xl shadow-soft p-4 sm:p-8 text-center">
                    <CheckCircleIcon className="w-12 h-12 sm:w-16 sm:h-16 text-gray-400 mx-auto mb-4" />
                    <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-2">Eligibility Not Available</h3>
                    <p className="text-gray-600 text-sm sm:text-base">Eligibility details are not available for this course.</p>
                </div>
            )
        }

        return (
            <div className="space-y-6 sm:space-y-8">
                {/* Eligibility Content */}
                <div className="bg-white rounded-xl shadow-soft p-4 sm:p-8">
                    <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4 sm:mb-6">Eligibility Criteria</h3>
                    <div className="prose max-w-none prose-sm sm:prose-base">
                        <div dangerouslySetInnerHTML={{ __html: eligibility || course.eligibility }} />
                    </div>
                </div>
            </div>
        )
    }

    const renderFeeStructureTab = () => {
        const feeStructure = feeStructureData?.data?.fee_structure

        if (!feeStructure && !course.feeStructure) {
            return (
                <div className="bg-white rounded-xl shadow-soft p-4 sm:p-8 text-center">
                    <CurrencyRupeeIcon className="w-12 h-12 sm:w-16 sm:h-16 text-gray-400 mx-auto mb-4" />
                    <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-2">Fee Structure Not Available</h3>
                    <p className="text-gray-600 text-sm sm:text-base">Fee structure details are not available for this course.</p>
                </div>
            )
        }

        return (
            <div className="space-y-6 sm:space-y-8">
                {/* Fee Structure Content */}
                <div className="bg-white rounded-xl shadow-soft p-4 sm:p-8">
                    <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4 sm:mb-6">Fee Structure</h3>
                    <div className="prose max-w-none prose-sm sm:prose-base">
                        <div dangerouslySetInnerHTML={{ __html: feeStructure || course.feeStructure }} />
                    </div>
                </div>

                {/* University Fee Range */}
                {course.universityId?.fee_range && (
                    <div className="bg-white rounded-xl shadow-soft p-4 sm:p-8">
                        <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4 sm:mb-6">University Fee Range</h3>
                        <div className="bg-gradient-to-r from-primary-50 to-secondary-50 rounded-lg p-4 sm:p-6">
                            <div className="flex items-center space-x-3">
                                <CurrencyRupeeIcon className="w-6 h-6 sm:w-8 sm:h-8 text-primary-600" />
                                <div>
                                    <h4 className="text-lg sm:text-xl font-semibold text-gray-900">
                                        {course.universityId.fee_range.type === 'below_2_lakhs' ? 'Below ₹2 Lakhs' : 'Above ₹2 Lakhs'}
                                    </h4>
                                    <p className="text-gray-600 text-sm sm:text-base">General fee range for courses at this university</p>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        )
    }

    const renderPlacementTab = () => {
        if (!course.universityId) {
            return (
                <div className="bg-white rounded-xl shadow-soft p-8 text-center">
                    <TrophyIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">Placement Information Not Available</h3>
                    <p className="text-gray-600">University information is not available for this course.</p>
                </div>
            )
        }

        const hasPlacementData = course.universityId.placements ||
            (course.universityId.top_recruiters && course.universityId.top_recruiters.length > 0)

        if (!hasPlacementData) {
            return (
                <div className="bg-white rounded-xl shadow-soft p-8 text-center">
                    <TrophyIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">Placement Information Not Available</h3>
                    <p className="text-gray-600 mb-4">Placement details are not available for this university.</p>
                    <div className="bg-blue-50 rounded-lg p-4">
                        <p className="text-blue-800 text-sm">
                            <strong>Note:</strong> For detailed placement information, please contact the university directly or visit their official website.
                        </p>
                    </div>
                </div>
            )
        }

        return (
            <div className="space-y-8">
                {/* University Placement Information */}
                {course.universityId.placements && (
                    <div className="bg-white rounded-xl shadow-soft p-4 sm:p-8 mb-6 sm:mb-8">
                        <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4 sm:mb-6">University Placement Information</h3>
                        <div className="prose max-w-none prose-sm sm:prose-base">
                            <div dangerouslySetInnerHTML={{ __html: course.universityId.placements }} />
                        </div>
                    </div>
                )}

                {/* Top Recruiters */}
                {course.universityId.top_recruiters && course.universityId.top_recruiters.length > 0 && (
                    <div className="bg-white rounded-xl shadow-soft p-4 sm:p-8 mb-6 sm:mb-8">
                        <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4 sm:mb-6">Top Recruiters</h3>
                        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
                            {course.universityId.top_recruiters.map((recruiter, index) => (
                                <div key={index} className="text-center p-3 sm:p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                                    {recruiter.logo && (
                                        <img
                                            src={recruiter.logo}
                                            alt={recruiter.company_name}
                                            className="w-8 h-8 sm:w-12 sm:h-12 object-contain mx-auto mb-2"
                                        />
                                    )}
                                    <h4 className="font-semibold text-gray-900 text-xs sm:text-sm">{recruiter.company_name}</h4>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* University Information */}
                <div className="bg-white rounded-xl shadow-soft p-4 sm:p-8 mb-6 sm:mb-8">
                    <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4 sm:mb-6">University Details</h3>
                    <div className="flex flex-col sm:flex-row items-start sm:items-center space-y-4 sm:space-y-0 sm:space-x-4">
                        {course.universityId.logo?.url && (
                            <img
                                src={course.universityId.logo.url}
                                alt={course.universityId.name}
                                className="w-12 h-12 sm:w-16 sm:h-16 object-contain flex-shrink-0"
                            />
                        )}
                        <div className="flex-1 min-w-0">
                            <h4 className="text-lg sm:text-xl font-semibold text-gray-900">{course.universityId.name}</h4>

                            <Link
                                to={`/universities/${course.universityId.slug}`}
                                className="text-primary-600 hover:text-primary-700 font-medium text-sm sm:text-base"
                            >
                                View University Details →
                            </Link>
                        </div>
                    </div>
                </div>

                {/* Contact Information */}
                <div className="bg-gradient-to-r from-primary-50 to-secondary-50 rounded-xl p-4 sm:p-8">
                    <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-3 sm:mb-4">Need More Information?</h3>
                    <p className="text-gray-700 mb-4 text-sm sm:text-base">
                        For detailed placement statistics, salary packages, and recruitment processes,
                        we recommend contacting the university directly or visiting their official website.
                    </p>
                    <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
                        <button
                            onClick={() => handleEnquiryClick('enquiry')}
                            className="bg-primary-600 text-white px-4 sm:px-6 py-2 sm:py-3 rounded-lg font-semibold hover:bg-primary-700 transition-colors text-sm sm:text-base"
                        >
                            Contact University
                        </button>
                        <button
                            onClick={() => handleEnquiryClick('brochure')}
                            className="bg-gray-600 text-white px-4 sm:px-6 py-2 sm:py-3 rounded-lg font-semibold hover:bg-gray-700 transition-colors text-sm sm:text-base"
                        >
                            Download Brochure
                        </button>
                    </div>
                </div>
            </div>
        )
    }

    const renderScholarshipsTab = () => {
        const scholarships = scholarshipsData?.data?.scholarships

        if (!scholarships && !course.scholarships) {
            return (
                <div className="bg-white rounded-xl shadow-soft p-4 sm:p-8 text-center">
                    <TrophyIcon className="w-12 h-12 sm:w-16 sm:h-16 text-gray-400 mx-auto mb-4" />
                    <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-2">No Scholarships Available</h3>
                    <p className="text-gray-600 text-sm sm:text-base">Currently, there are no scholarships available for this course.</p>
                </div>
            )
        }

        return (
            <div className="space-y-6 sm:space-y-8">
                {/* Scholarships Content */}
                <div className="bg-white rounded-xl shadow-soft p-4 sm:p-8">
                    <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4 sm:mb-6">Scholarships & Financial Aid</h3>
                    <div className="prose max-w-none prose-sm sm:prose-base">
                        <div dangerouslySetInnerHTML={{ __html: scholarships || course.scholarships }} />
                    </div>
                </div>
            </div>
        )
    }

    const tabs = [
        { id: 'overview', label: 'Overview', icon: BookOpenIcon },
        { id: 'curriculum', label: 'Curriculum', icon: DocumentTextIcon },
        { id: 'eligibility', label: 'Eligibility', icon: CheckCircleIcon },
        { id: 'fee-structure', label: 'Fee Structure', icon: CurrencyRupeeIcon },
        { id: 'placement', label: 'Placement', icon: TrophyIcon },
        { id: 'scholarships', label: 'Scholarships', icon: StarIcon }
    ]

    if (isLoading) {
        return <LoadingSpinner />
    }

    if (error) {
        return <ErrorMessage message="Failed to load course details" />
    }

    if (!course) {
        return <ErrorMessage message="Course not found" />
    }

    return (
        <>
            <Helmet>
                <title>{course.degreeId ? course.degreeId.name : `${course.level} - ${course.specialization ? course.specialization.join(', ') : 'Course'}`} | Course Details | Margdarshak</title>
                <meta name="description" content={`${course.degreeId ? course.degreeId.name : course.level} course in ${course.specialization ? course.specialization.join(', ') : 'various specializations'} at ${course.universityId?.name}`} />
                <meta name="keywords" content={`${course.level}, ${course.specialization ? course.specialization.join(', ') : 'course'}, ${course.universityId?.name}, education`} />
            </Helmet>

            {/* Redesigned Course Hero Section */}
            <div className="bg-white relative overflow-hidden pt-20 sm:pt-24 md:pt-28 pb-12 sm:pb-16 md:pb-20 mt-16 border-b border-gray-100">
                {/* Subtle Grid Pattern Background */}
                <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: 'radial-gradient(#000 1px, transparent 1px)', backgroundSize: '32px 32px' }}></div>

                {/* Decorative Elements */}
                <div className="absolute top-0 right-0 -mt-20 -mr-20 w-80 h-80 bg-primary-50 rounded-full blur-3xl opacity-50"></div>
                <div className="absolute bottom-0 left-0 -mb-20 -ml-20 w-72 h-72 bg-accent-50 rounded-full blur-3xl opacity-50"></div>

                <div className="relative z-10 container mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center">
                        {/* Left Column: Course & University Info */}
                        <div className="lg:col-span-8 space-y-6">
                            <motion.div
                                initial={{ opacity: 0, scale: 0.95 }}
                                animate={{ opacity: 1, scale: 1 }}
                                transition={{ duration: 0.5 }}
                                className="inline-flex items-center space-x-2 bg-primary-50 text-primary-700 px-4 py-1.5 rounded-full text-xs sm:text-sm font-bold uppercase tracking-widest border border-primary-100"
                            >
                                <AcademicCapIcon className="w-4 h-4 sm:w-5 sm:h-5" />
                                <span>{course.level || 'Premium Program'}</span>
                            </motion.div>

                            {/* Course Title */}
                            <motion.h1
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.6, delay: 0.1 }}
                                className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-black text-gray-900 leading-tight tracking-tight"
                            >
                                {course.degreeId ? course.degreeId.name : `${course.level} - ${course.specialization ? course.specialization.join(', ') : 'Course'}`}
                            </motion.h1>

                            {/* Specializations List */}
                            {course.specialization && course.specialization.length > 0 && (
                                <motion.p
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.6, delay: 0.2 }}
                                    className="text-base sm:text-lg lg:text-xl text-gray-600 leading-relaxed max-w-3xl"
                                >
                                    <b>Specializations:</b> {course.specialization.join(' • ')}
                                </motion.p>
                            )}

                            {/* University Banner Info */}
                            {course.universityId && (
                                <motion.div
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.6, delay: 0.3 }}
                                    className="flex flex-col sm:flex-row items-start sm:items-center gap-4 bg-white/60 p-4 rounded-2xl border border-gray-100 shadow-sm backdrop-blur-sm mt-4"
                                >
                                    {course.universityId.logo?.url && (
                                        <div className="w-14 h-14 bg-white rounded-xl shadow-sm border border-gray-100 p-1.5 flex-shrink-0 flex items-center justify-center">
                                            <img src={course.universityId.logo.url} alt="Logo" className="w-full h-full object-contain" />
                                        </div>
                                    )}
                                    <div>
                                        <Link to={`/universities/${course.universityId.slug}`} className="group inline-flex items-center">
                                            <h3 className="text-lg font-bold text-gray-900 group-hover:text-primary-600 transition-colors">
                                                {course.universityId.name}
                                            </h3>
                                            <BuildingOfficeIcon className="w-4 h-4 ml-2 text-gray-400 group-hover:text-primary-500 transition-colors" />
                                        </Link>
                                    </div>

                                    {/* University Approvals Row */}
                                    {course.universityId?.approvals && course.universityId.approvals.length > 0 && (
                                        <div className="flex flex-wrap gap-2 ml-0 sm:ml-auto mt-2 sm:mt-0">
                                            {course.universityId.approvals.slice(0, 3).map((approval, idx) => (
                                                <span key={idx} className="inline-flex items-center space-x-1 bg-green-50 text-green-700 border border-green-200 px-2 py-0.5 rounded text-xs font-bold uppercase tracking-wider">
                                                    <CheckCircleIcon className="w-3.5 h-3.5 shrink-0" />
                                                    <span>{approval.name}</span>
                                                </span>
                                            ))}
                                            {course.universityId.approvals.length > 3 && (
                                                <span className="inline-flex items-center space-x-1 bg-gray-50 text-gray-600 border border-gray-200 px-2 py-0.5 rounded text-xs font-bold uppercase tracking-wider">
                                                    +{course.universityId.approvals.length - 3} More
                                                </span>
                                            )}
                                        </div>
                                    )}
                                </motion.div>
                            )}
                        </div>

                        {/* Right Column: Key Stats & Actions */}
                        <div className="lg:col-span-4 space-y-4">
                            <motion.div
                                initial={{ opacity: 0, scale: 0.95 }}
                                animate={{ opacity: 1, scale: 1 }}
                                transition={{ duration: 0.6, delay: 0.3 }}
                                className="bg-white p-6 rounded-3xl shadow-xl shadow-primary-900/5 border border-primary-100"
                            >
                                <h4 className="text-sm font-bold text-gray-500 uppercase tracking-widest mb-4">Course Highlights</h4>

                                <div className="space-y-4 mb-8">
                                    {course.duration && (
                                        <div className="flex items-center text-gray-700">
                                            <div className="w-10 h-10 rounded-full bg-primary-50 flex items-center justify-center mr-3 shrink-0">
                                                <ClockIcon className="w-5 h-5 text-primary-600" />
                                            </div>
                                            <div>
                                                <p className="text-xs text-gray-500 font-semibold uppercase">Duration</p>
                                                <p className="font-bold text-gray-900">{course.duration}</p>
                                            </div>
                                        </div>
                                    )}
                                    {course.level && (
                                        <div className="flex items-center text-gray-700">
                                            <div className="w-10 h-10 rounded-full bg-accent-50 flex items-center justify-center mr-3 shrink-0">
                                                <AcademicCapIcon className="w-5 h-5 text-accent-600" />
                                            </div>
                                            <div>
                                                <p className="text-xs text-gray-500 font-semibold uppercase">Degree Level</p>
                                                <p className="font-bold text-gray-900">{course.level}</p>
                                            </div>
                                        </div>
                                    )}
                                    {course.universityId?.fee_range && (
                                        <div className="flex items-center text-gray-700">
                                            <div className="w-10 h-10 rounded-full bg-blue-50 flex items-center justify-center mr-3 shrink-0">
                                                <CurrencyRupeeIcon className="w-5 h-5 text-blue-600" />
                                            </div>
                                            <div>
                                                <p className="text-xs text-gray-500 font-semibold uppercase">Fee Range</p>
                                                <p className="font-bold text-gray-900">
                                                    {course.universityId.fee_range.type === 'below_2_lakhs' ? 'Below 2 Lakhs' :
                                                        course.universityId.fee_range.type === 'above_2_lakhs' ? 'Above 2 Lakhs' :
                                                            course.universityId.fee_range.type || 'Contact for Details'}
                                                </p>
                                            </div>
                                        </div>
                                    )}
                                    {course.universityId?.placements?.highest_package && (
                                        <div className="flex items-center text-gray-700">
                                            <div className="w-10 h-10 rounded-full bg-yellow-50 flex items-center justify-center mr-3 shrink-0">
                                                <StarIcon className="w-5 h-5 text-yellow-600" />
                                            </div>
                                            <div>
                                                <p className="text-xs text-gray-500 font-semibold uppercase">Highest Package</p>
                                                <p className="font-bold text-gray-900">{course.universityId.placements.highest_package}</p>
                                            </div>
                                        </div>
                                    )}
                                    {course.degreeId && course.degreeId.duration && course.degreeId.duration !== course.duration && (
                                        <div className="flex items-center text-gray-700">
                                            <div className="w-10 h-10 rounded-full bg-green-50 flex items-center justify-center mr-3 shrink-0">
                                                <DocumentTextIcon className="w-5 h-5 text-green-600" />
                                            </div>
                                            <div>
                                                <p className="text-xs text-gray-500 font-semibold uppercase">Total Time</p>
                                                <p className="font-bold text-gray-900">{course.degreeId.duration}</p>
                                            </div>
                                        </div>
                                    )}
                                </div>

                                {/* Action Buttons */}
                                <div className="space-y-3">
                                    <button
                                        onClick={() => handleEnquiryClick('enquiry')}
                                        className="w-full bg-primary-600 text-white px-6 py-3.5 rounded-xl font-bold hover:bg-primary-700 transition-all duration-300 shadow-md active:scale-[0.98]"
                                    >
                                        Enquire Now
                                    </button>
                                    <button
                                        onClick={() => handleEnquiryClick('brochure')}
                                        className="w-full bg-white text-gray-800 px-6 py-3.5 rounded-xl font-bold hover:bg-gray-50 transition-all duration-300 shadow-sm border border-gray-200 flex justify-center items-center active:scale-[0.98]"
                                    >
                                        <ArrowDownTrayIcon className="w-5 h-5 mr-2 text-primary-600" />
                                        Download Brochure
                                    </button>
                                </div>
                            </motion.div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Main Content */}
            <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
                {/* Tab Navigation */}
                <div className="bg-white rounded-xl shadow-soft p-2 mb-6 sm:mb-8">
                    <div className="flex flex-wrap gap-1 sm:gap-2">
                        {tabs.map((tab) => {
                            const Icon = tab.icon
                            return (
                                <button
                                    key={tab.id}
                                    onClick={() => setActiveTab(tab.id)}
                                    className={`flex items-center space-x-1 sm:space-x-2 px-2 sm:px-4 py-2 rounded-lg font-medium transition-colors text-xs sm:text-sm ${activeTab === tab.id
                                        ? 'bg-primary-600 text-white'
                                        : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                                        }`}
                                >
                                    <Icon className="w-3 h-3 sm:w-4 sm:h-4" />
                                    <span className="hidden xs:inline">{tab.label}</span>
                                </button>
                            )
                        })}
                    </div>
                </div>

                {/* Tab Content */}
                <AnimatePresence mode="wait">
                    <motion.div
                        key={activeTab}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -20 }}
                        transition={{ duration: 0.3 }}
                    >
                        {activeTab === 'overview' && renderOverviewTab()}
                        {activeTab === 'curriculum' && renderCurriculumTab()}
                        {activeTab === 'eligibility' && renderEligibilityTab()}
                        {activeTab === 'fee-structure' && renderFeeStructureTab()}
                        {activeTab === 'placement' && renderPlacementTab()}
                        {activeTab === 'scholarships' && renderScholarshipsTab()}
                    </motion.div>
                </AnimatePresence>
            </div>

            {/* Enquiry Modal */}
            <EnquiryModal
                isOpen={enquiryModal.isOpen}
                onClose={handleCloseEnquiryModal}
                title={
                    enquiryModal.type === 'enquiry' ? 'Quick Enquiry' :
                        enquiryModal.type === 'brochure' ? 'Download Brochure' :
                            'Free Career Counselling'
                }
                source={enquiryModal.type}
                itemName={enquiryModal.course ? `${enquiryModal.course.degreeId ? enquiryModal.course.degreeId.name : `${enquiryModal.course.level} - ${enquiryModal.course.specialization ? enquiryModal.course.specialization.join(', ') : 'Course'}`}` : ''}
                itemType="course"
                itemId={enquiryModal.course?._id || null}
            />


        </>
    )
}

export default CourseDetail